/**
 * Program sorting utility
 * Standard order: Beginner 1 hour, Beginner 1.5 hours, Beginner 2 hours, Adv Beginner, U11, U13, U15+
 * One-time events are separated and shown after regular programs
 */

export const PROGRAM_SORT_ORDER = {
  'beginner - 1 hour': 1,
  'beginner 1 hour': 1,
  'beginner - 1hr': 1,
  'beginner 1hr': 1,
  'beginner - 1.5 hours': 2,
  'beginner 1.5 hours': 2,
  'beginner - 1.5hr': 2,
  'beginner 1.5hr': 2,
  'beginner - 2 hours': 3,
  'beginner 2 hours': 3,
  'beginner - 2hr': 3,
  'beginner 2hr': 3,
  'adv beginner': 4,
  'advanced beginner': 4,
  'u11': 5,
  'u11 development': 5,
  'u13': 6,
  'u13 development': 6,
  'u15': 7,
  'u15+': 7,
  'u15+ elite': 7,
  'u15 elite': 7,
};

/**
 * Get sort order for a program name
 * @param {string} name - Program name
 * @returns {number} Sort order (lower = first)
 */
export const getProgramSortOrder = (name) => {
  if (!name) return 999;
  const lowerName = name.toLowerCase().trim();
  
  // Check exact match first
  if (PROGRAM_SORT_ORDER[lowerName] !== undefined) {
    return PROGRAM_SORT_ORDER[lowerName];
  }
  
  // Check partial matches
  if (lowerName.includes('beginner') && lowerName.includes('1 hour')) return 1;
  if (lowerName.includes('beginner') && lowerName.includes('1hr')) return 1;
  if (lowerName.includes('beginner') && lowerName.includes('1.5')) return 2;
  if (lowerName.includes('beginner') && lowerName.includes('2 hour')) return 3;
  if (lowerName.includes('beginner') && lowerName.includes('2hr')) return 3;
  if (lowerName.includes('adv') && lowerName.includes('beginner')) return 4;
  if (lowerName.includes('u11')) return 5;
  if (lowerName.includes('u13')) return 6;
  if (lowerName.includes('u15')) return 7;
  
  return 100; // Default for unknown programs
};

/**
 * Sort programs by standard order
 * @param {Array} programs - Array of program objects
 * @returns {Object} { regularPrograms: [], oneTimeEvents: [] }
 */
export const sortAndSeparatePrograms = (programs) => {
  if (!programs || !Array.isArray(programs)) {
    return { regularPrograms: [], oneTimeEvents: [] };
  }
  
  const regularPrograms = [];
  const oneTimeEvents = [];
  
  programs.forEach(program => {
    // Check if it's a one-time event (has specific dates or is marked as event)
    const isOneTime = program.is_one_time || 
                      program.program_type === 'event' ||
                      program.billing_type === 'one_time';
    
    if (isOneTime) {
      oneTimeEvents.push(program);
    } else {
      regularPrograms.push(program);
    }
  });
  
  // Sort regular programs by standard order
  regularPrograms.sort((a, b) => {
    const aOrder = getProgramSortOrder(a.name);
    const bOrder = getProgramSortOrder(b.name);
    if (aOrder !== bOrder) return aOrder - bOrder;
    return (a.name || '').localeCompare(b.name || '');
  });
  
  // Sort one-time events by start date or name
  oneTimeEvents.sort((a, b) => {
    if (a.start_date && b.start_date) {
      return new Date(a.start_date) - new Date(b.start_date);
    }
    return (a.name || '').localeCompare(b.name || '');
  });
  
  return { regularPrograms, oneTimeEvents };
};

/**
 * Sort programs (simple version - all in one array)
 * @param {Array} programs - Array of program objects
 * @returns {Array} Sorted programs
 */
export const sortPrograms = (programs) => {
  if (!programs || !Array.isArray(programs)) return [];
  
  return [...programs].sort((a, b) => {
    // One-time events go last
    const aIsOneTime = a.is_one_time || a.program_type === 'event' || a.billing_type === 'one_time';
    const bIsOneTime = b.is_one_time || b.program_type === 'event' || b.billing_type === 'one_time';
    
    if (aIsOneTime && !bIsOneTime) return 1;
    if (!aIsOneTime && bIsOneTime) return -1;
    
    // Both are same type, sort by order
    const aOrder = getProgramSortOrder(a.name);
    const bOrder = getProgramSortOrder(b.name);
    if (aOrder !== bOrder) return aOrder - bOrder;
    
    return (a.name || '').localeCompare(b.name || '');
  });
};

export default { PROGRAM_SORT_ORDER, getProgramSortOrder, sortAndSeparatePrograms, sortPrograms };
